import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiMoreHorizontal } from "react-icons/fi";
import { Eye, Pencil, Trash2, Plus, Link } from "lucide-react";
import { Col, Row, Alert, Spinner } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumb from "../../components/Common/Bredcrump";
import UnifiedDocumentModal from "../../components/Common/UnifiedDocumentModal";
import ReusableTable from "../../components/Common/BaseTable";
import { getFreeFormContent, deleteFreeFormContent, getShopifyPage } from "../../services/freeFormContentService";
import "./FreeFormContent.css";

const FreeFormContent = () => {
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);
  const [contentList, setContentList] = useState([]);
  const [originalContentList, setOriginalContentList] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editingRowData, setEditingRowData] = useState(null); // Store row data for edit flow
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoadingEditData, setIsLoadingEditData] = useState(false); // Loading state for edit data fetching
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total_count: 0,
    total_pages: 0
  });
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', order: 'desc' });
  const [filterColumn, setFilterColumn] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [sourceFilter, setSourceFilter] = useState("Local"); // "Local" or "Shopify"

  const fetchContent = async (page = 1, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch content with source filter
      const freeFormResponse = await getFreeFormContent({
        page,
        per_page: pagination.per_page,
        source: sourceFilter,
        ...filters
      });
      
      if (freeFormResponse.success) {
        const contentData = freeFormResponse.data || [];
        
        // Ensure is_shopify_page is set correctly based on source
        const transformedContent = contentData.map(item => ({
          ...item,
          is_shopify_page: sourceFilter === "Shopify"
        }));
        
        setContentList(transformedContent);
        setOriginalContentList(transformedContent); // Store original data for search
        setPagination(freeFormResponse.pagination);
      } else {
        // Check if it's a 400 or 500 error
        const status = freeFormResponse?.status || freeFormResponse?.response?.status;
        if (status === 400 || status === 500) {
          setError("No data found");
          setContentList([]);
          setOriginalContentList([]);
        } else {
          setError(freeFormResponse.message || 'Failed to fetch content');
        }
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      // Check if it's a 400 or 500 error
      const status = err?.response?.status;
      if (status === 400 || status === 500) {
        setError("No data found");
        setContentList([]);
        setOriginalContentList([]);
      } else {
        setError(err.message || 'Failed to fetch content');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset to page 1 when source filter changes
    setPagination(prev => ({
      ...prev,
      current_page: 1
    }));
    fetchContent(1);
  }, [sourceFilter]);

  const handleSearch = (searchValue) => {
    if (!searchValue.trim()) {
      // If no search value, restore original content
      setContentList(originalContentList);
      return;
    }
    
    // Implement client-side search since we have all data
    const searchLower = searchValue.toLowerCase();
    
    // Filter the original content list based on search
    const filteredContent = originalContentList.filter(item => {
      const searchableFields = [
        item.name || '',
        item.title || '',
        item.prompt || '',
        item.language || '',
        item.location_focus || '',
        item.status || '',
        item.platform || '',
        item.page_type || ''
      ].map(field => field.toString().toLowerCase());
      
      return searchableFields.some(field => field.includes(searchLower));
    });
    
    setContentList(filteredContent);
  };

  const handleSort = (sortOption) => {
    let newSortConfig = { key: 'created_at', order: 'desc' };
    
    switch (sortOption) {
      case 'newest':
        newSortConfig = { key: 'created_at', order: 'desc' };
        break;
      case 'oldest':
        newSortConfig = { key: 'created_at', order: 'asc' };
        break;
      case 'name_asc':
        newSortConfig = { key: 'name', order: 'asc' };
        break;
      case 'name_desc':
        newSortConfig = { key: 'name', order: 'desc' };
        break;
      case 'status_asc':
        newSortConfig = { key: 'status', order: 'asc' };
        break;
      case 'status_desc':
        newSortConfig = { key: 'status', order: 'desc' };
        break;
      case 'language_asc':
        newSortConfig = { key: 'language', order: 'asc' };
        break;
      case 'language_desc':
        newSortConfig = { key: 'language', order: 'desc' };
        break;
      default:
        newSortConfig = { key: 'created_at', order: 'desc' };
    }
    
    setSortConfig(newSortConfig);
    
    // Apply sorting to current content list
    const sortedContent = [...contentList].sort((a, b) => {
      let aValue = a[newSortConfig.key];
      let bValue = b[newSortConfig.key];
      
      // Handle date sorting
      if (newSortConfig.key === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (newSortConfig.order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setContentList(sortedContent);
  };

  const handleFilter = (column, value) => {
    setFilterColumn(column);
    setFilterValue(value);
    
    if (!value.trim()) {
      // If no filter value, restore original content
      setContentList(originalContentList);
      return;
    }
    
    // Apply filtering based on column and value
    const filteredContent = originalContentList.filter(item => {
      const itemValue = item[column];
      if (!itemValue) return false;
      
      // Special handling for date filtering
      if (column === 'created_at') {
        // For date filtering, we need to check both the raw date and the formatted date
        const rawDate = item.created_at;
        const formattedDate = item.created;
        
        // Check if the search value matches either the raw date or formatted date
        const searchLower = value.toLowerCase();
        const rawDateStr = rawDate ? new Date(rawDate).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          year: 'numeric' 
        }).toLowerCase() : '';
        const formattedDateStr = formattedDate ? formattedDate.toLowerCase() : '';
        
        return rawDateStr.includes(searchLower) || formattedDateStr.includes(searchLower);
      }
      
      return itemValue.toString().toLowerCase().includes(value.toLowerCase());
    });
    
    setContentList(filteredContent);
  };

  const handleDelete = async (selectedRows) => {
    try {
      // Filter out Shopify pages (they can't be deleted from here)
      const freeFormRows = selectedRows.filter(rowId => !rowId.toString().startsWith('shopify_'));
      const shopifyRows = selectedRows.filter(rowId => rowId.toString().startsWith('shopify_'));
      
      if (shopifyRows.length > 0) {
        toast.warning(`${shopifyRows.length} Shopify page(s) cannot be deleted from this interface.`);
      }
      
      if (freeFormRows.length > 0) {
        const deletePromises = freeFormRows.map(rowId => 
          deleteFreeFormContent(rowId)
        );
        
        await Promise.all(deletePromises);
        
        // Refresh the content list
        fetchContent(pagination.current_page);
        
        // Show success message
        toast.success(`${freeFormRows.length} item(s) deleted successfully`);
      }
    } catch (error) {
      console.error('Error deleting items:', error);
      toast.error('Failed to delete some items: ' + error.message);
    }
  };

  const handleDuplicate = (selectedRows) => {
    // Filter out Shopify pages (they can't be duplicated from here)
    const freeFormRows = selectedRows.filter(rowId => !rowId.toString().startsWith('shopify_'));
    const shopifyRows = selectedRows.filter(rowId => rowId.toString().startsWith('shopify_'));
    
    if (shopifyRows.length > 0) {
      toast.warning(`${shopifyRows.length} Shopify page(s) cannot be duplicated from this interface.`);
    }
    
    if (freeFormRows.length > 0) {
      // TODO: Implement duplicate functionality for free form content
      toast.info('Duplicate functionality coming soon');
    }
  };

  const handleMenuOption = async (action, rowData) => {
    if (action === 'edit') {
      if (rowData.is_shopify_page) {
        try {
          setIsLoadingEditData(true);
          toast.info('Loading page data...');
          const resourceId = rowData.id.replace('shopify_', '');
          const resourceType = rowData.page_type;
          
          const shopifyPageData = await getShopifyPage(resourceId, resourceType);
          
          setEditingRowData(shopifyPageData);
          setShowDocumentModal(true);
          toast.success('Page data loaded successfully!');
          
        } catch (error) {
          console.error('Error fetching Shopify page data:', error);
          toast.error('Failed to load page data. Using cached data instead.');
          
          setEditingRowData(rowData);
          setShowDocumentModal(true);
        } finally {
          setIsLoadingEditData(false);
        }
      } else {
        // For regular free form content, just store the row data
        setEditingRowData(rowData);
        setShowDocumentModal(true);
      }
    } else if (action === 'preview') {
      if (rowData.is_shopify_page) {
        // For Shopify pages, use state-based navigation
        navigate('/skills/free-form-content/preview', { state: { contentData: rowData } });
      } else {
        // For free form content, use ID-based navigation with state data
        navigate(`/skills/free-form-content/preview/${rowData.id}`, { state: { contentData: rowData } });
      }
    } else if (action === 'share') {
      // Share functionality for local content
      if (!rowData.id) {
        toast.warning('Cannot share content without an ID.');
        return;
      }
      
      // Create share URL - same as preview page
      const shareUrl = `${window.location.origin}/skills/free-form-content/preview/${rowData.id}`;
      
      // Copy to clipboard
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          toast.success('Share link copied to clipboard!');
        })
        .catch(() => {
          toast.error('Failed to copy link. Please copy manually: ' + shareUrl);
        });
    } else if (action === 'delete') {
      if (window.confirm('Are you sure you want to delete this content?')) {
        handleDelete([rowData.id]);
      }
    }
    setSelectedRow(null);
  };

  const handleCreateNew = () => {
    // Clear editing row data for create flow
    setEditingRowData(null);
    setShowDocumentModal(true);
  };

  const handleDocumentModalClose = () => {
    setShowDocumentModal(false);
    setEditingRowData(null);
  };

  const handlePageChange = (newPage) => {
    fetchContent(newPage);
  };

  // Language mapping function to convert codes to full names
  const getLanguageDisplayName = (languageCode) => {
    const languageMap = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'ru': 'Russian',
      'ja': 'Japanese',
      'ko': 'Korean',
      'zh': 'Chinese',
      'hi': 'Hindi',
      'ar': 'Arabic',
      'tr': 'Turkish',
      'nl': 'Dutch',
      'sv': 'Swedish',
      'no': 'Norwegian',
      'da': 'Danish',
      'fi': 'Finnish',
      'pl': 'Polish',
      'cs': 'Czech',
      'sk': 'Slovak',
      'hu': 'Hungarian',
      'ro': 'Romanian',
      'bg': 'Bulgarian',
      'hr': 'Croatian',
      'sl': 'Slovenian',
      'et': 'Estonian',
      'lv': 'Latvian',
      'lt': 'Lithuanian'
    };
    
    return languageMap[languageCode] || languageCode;
  };

  const data = contentList;

  const columns = [
    {
      id: "selection",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={() => table.toggleAllPageRowsSelected()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={() => row.toggleSelected()}
        />
      ),
      size: "5%",
      className: "selection-column",
    },
    {
      accessorKey: "name",
      header: "Name",
      size: 200,
      className: "name-column",
      cell: ({ getValue, row }) => {
        const name = getValue();
        const isShopifyPage = row.original.is_shopify_page;
        return (
          <div>
            {name}
          </div>
        );
      },
    },
    {
      accessorKey: "platform",
      header: "Platform",
      size: 120,
      className: "platform-column",
      cell: ({ getValue, row }) => {
        const platform = getValue();
        const isShopifyPage = row.original.is_shopify_page;
        
        if (isShopifyPage) {
          return platform || 'Shopify';
        } else {
          return 'Local';
        }
      },
    },
    {
      accessorKey: "created",
      header: "Created",
      size: 150,
      className: "created-column",
    },
    {
      accessorKey: "language",
      header: "Language",
      size: 120,
      className: "language-column",
      cell: ({ getValue, row }) => {
        const languageCode = getValue();
        const isShopifyPage = row.original.is_shopify_page;
        
        if (isShopifyPage) {
          return languageCode;
        } else {
          return getLanguageDisplayName(languageCode);
        }
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 120,
      className: "status-column",
      cell: ({ getValue, row }) => {
        const status = getValue();
        return status;
      },
    },
    {
      header: "Actions",
      className: "actions-column",
      cell: ({ row }) => {
        const isShopifyPage = row.original.is_shopify_page;
        
        return (
          <div style={{ position: 'relative' }}>
            <FiMoreHorizontal 
              className="three-dot-menu" 
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRow(selectedRow === row.index ? null : row.index);
              }}
              style={{ cursor: 'pointer' }}
            />
            {selectedRow === row.index && (
              <div className="action-menu">
                {row.original.is_shopify_page ? (
                  // Shopify page actions
                  <>
                    <button 
                      className="menu-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (row.original.page_url) {
                          window.open(row.original.page_url, '_blank');
                        }
                      }}
                    >
                      <Eye size={16} />
                      <span>View Page</span>
                    </button>
                    
                    <button 
                      className="menu-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOption('edit', row.original);
                      }}
                      disabled={loading || isLoadingEditData}
                    >
                      {loading || isLoadingEditData ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Pencil size={16} />
                      )}
                      <span>Edit</span>
                    </button>
                    
                    <button 
                      className="menu-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Copy page URL to clipboard
                        if (row.original.page_url) {
                          navigator.clipboard.writeText(row.original.page_url);
                          toast.success('Page URL copied to clipboard!');
                        }
                      }}
                    >
                      <Link size={16} />
                      <span>Copy URL</span>
                    </button>
                  </>
                ) : (
                  // Local content actions
                  <>
                    <button 
                      className="menu-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOption('preview', row.original);
                      }}
                    >
                      <Eye size={16} />
                      <span>Preview</span>
                    </button>
                    
                    <button 
                      className="menu-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOption('edit', row.original);
                      }}
                      disabled={isLoadingEditData}
                    >
                      {isLoadingEditData ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <Pencil size={16} />
                      )}
                      <span>Edit</span>
                    </button>
                    
                    {/* Share button - only for local content (is_shopify_page: false) */}
                    {!row.original.is_shopify_page && (
                      <button 
                        className="menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOption('share', row.original);
                        }}
                      >
                        <Link size={16} />
                        <span>Share</span>
                      </button>
                    )}
                    
                    <button 
                      className="menu-item delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOption('delete', row.original);
                      }}
                    >
                      <Trash2 size={16} />
                      <span>Delete</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  if (loading && contentList.length === 0) {
    return (
      <div>
        <Breadcrumb />
        <div className="free-form-content-header">
          <div className="page-title">Free Form Content</div>
          <div className="header-actions">
            <button
              onClick={handleCreateNew}
              className="create-new-btn"
            >
              <Plus size={16} />
              Create New
            </button>
          </div>
        </div>
        <div className="text-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading content...</p>
        </div>
      </div>
    );
  }

  if (error && !loading) {
    // Show "No data found" message for 400/500 errors, otherwise show error alert
    if (error === "No data found") {
      return (
        <div>
          <ToastContainer />
          <Breadcrumb />
          <div className="free-form-content-header">
            <div className="page-title">Free Form Content</div>
            <div className="header-actions">
              <button
                onClick={handleCreateNew}
                className="create-new-btn"
              >
                <Plus size={16} />
                Create New
              </button>
            </div>
          </div>
          <div className="mt-3 border rounded">
            <div className="text-center p-4">
              <p className="text-muted">{error}</p>
            </div>
          </div>
          <UnifiedDocumentModal
            show={showDocumentModal}
            onHide={handleDocumentModalClose}
            initialTags=""
            initialDocList=""
            editingRowData={editingRowData}
          />
        </div>
      );
    }
    
    return (
      <div>
        <ToastContainer />
        <Breadcrumb />
        <div className="free-form-content-header">
          <div className="page-title">Free Form Content</div>
          <div className="header-actions">
            <button
              onClick={handleCreateNew}
              className="create-new-btn"
            >
              <Plus size={16} />
              Create New
            </button>
          </div>
        </div>
        <Alert variant="danger" className="mt-3">
          <strong>Error:</strong> {error}
          <div className="mt-2">
            <button 
              className="btn btn-outline-danger btn-sm"
              onClick={() => fetchContent()}
            >
              Try Again
            </button>
          </div>
        </Alert>
        <UnifiedDocumentModal
          show={showDocumentModal}
          onHide={handleDocumentModalClose}
          initialTags=""
          initialDocList=""
          editingRowData={editingRowData}
        />
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />
      <Breadcrumb />
      
      <div className="free-form-content-header">
        <div className="page-title">
          Free Form Content
          {contentList.length > 0 && (
            <div className="content-stats">
              <small className="text-muted">
                Showing {contentList.length} {sourceFilter} content item{contentList.length !== 1 ? 's' : ''}
              </small>
            </div>
          )}
        </div>
        <div className="header-actions">
          <button
            onClick={handleCreateNew}
            className="create-new-btn"
          >
            <Plus size={16} />
            Create New
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center mt-3">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Refreshing...</span>
        </div>
      )}

      <div className="mt-3" style={{ marginBottom: '16px' }}>
        <div className="d-flex align-items-center gap-2" style={{ marginBottom: '16px' }}>
          <label htmlFor="source-filter" style={{ marginBottom: 0, fontWeight: 500 }}>
            Filter by Source:
          </label>
          <select
            id="source-filter"
            className="form-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            style={{ width: 'auto', minWidth: '150px' }}
          >
            <option value="Local">Local</option>
            <option value="Shopify">Shopify</option>
          </select>
        </div>
        <ReusableTable
          data={data}
          columns={columns}
          onSearch={handleSearch}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onSort={handleSort}
          onFilter={handleFilter}
          gridView={false}
          filterColumn={filterColumn}
          setFilterColumn={setFilterColumn}
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          filterOptions={[
            { value: "name", label: "Name" },
            { value: "platform", label: "Platform" },
            { value: "language", label: "Language" },
            { value: "status", label: "Status" },
            { value: "created_at", label: "Created At" }
          ]}
        />
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of {pagination.total_count} entries
          </div>
          <div>
            <button
              className="btn btn-outline-primary btn-sm me-2"
              disabled={!pagination.has_prev}
              onClick={() => handlePageChange(pagination.current_page - 1)}
            >
              Previous
            </button>
            <span className="mx-2">
              Page {pagination.current_page} of {pagination.total_pages}
            </span>
            <button
              className="btn btn-outline-primary btn-sm ms-2"
              disabled={!pagination.has_next}
              onClick={() => handlePageChange(pagination.current_page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      <UnifiedDocumentModal
        show={showDocumentModal}
        onHide={handleDocumentModalClose}
        initialTags=""
        initialDocList=""
        editingRowData={editingRowData}
      />
    </div>
  );
};

export default FreeFormContent;
